import { useEffect, useState } from "react";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { UtilizationReportFilters } from "../../components/features/utilization-report/components/utilization-report-filters.tsx";
import { UtilizationReportTable } from "../../components/features/utilization-report/components/utilization-report-table.tsx";
import { UtilizationSummaryCards } from "../../components/features/utilization-report/components/utilization-summary-cards.tsx";
import { useUtilizationReport } from "../../components/features/utilization-report/hooks/use-utilization-report.ts";
import type { UtilizationFilters } from "../../components/features/utilization-report/types.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useDepartmentOptions } from "../../hooks/use-department-options.ts";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import type { PageProps } from "../../types/page.ts";

import styles from "./utilization-report-page.module.css";

const EMPTY_FILTERS: UtilizationFilters = {
  employeeId: null,
  departmentId: null,
  category: null,
};

export default function UtilizationReportPage({ title, pageMeta }: PageProps) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const employees = useEmployeeNameOptions();
  const departments = useDepartmentOptions();
  const [employeeId, setEmployeeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [filters, setFilters] = useState<UtilizationFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const report = useUtilizationReport(filters, page, pageSize);
  const lookupsLoading = employees.loading || departments.loading;
  const lookupError = employees.error || departments.error;

  const pageTitle = pageMeta?.title || title || "Utilization Report";

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  useEffect(() => {
    const allFiltersCleared = !employeeId && !departmentId;
    const hasAppliedFilters =
      filters.employeeId != null || filters.departmentId != null;

    if (allFiltersCleared && hasAppliedFilters) {
      setFilters(EMPTY_FILTERS);
      setPage(1);
    }
  }, [
    employeeId,
    departmentId,
    filters.employeeId,
    filters.departmentId,
  ]);

  const clearFilters = () => {
    setEmployeeId("");
    setDepartmentId("");
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const applyFilters = () => {
    setFilters((current) => ({
      ...current,
      employeeId: employeeId ? Number(employeeId) : null,
      departmentId: departmentId ? Number(departmentId) : null,
    }));
    setPage(1);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <PageShell className={styles.page}>
      <UtilizationSummaryCards
        summary={report.summary}
        selectedCategory={filters.category}
        loading={report.loading}
        onSelect={(category) => {
          setFilters((current) => ({ ...current, category }));
          setPage(1);
        }}
      />

      <UtilizationReportFilters
        employeeId={employeeId}
        departmentId={departmentId}
        employeeOptions={employees.options}
        departmentOptions={departments.options}
        loading={lookupsLoading || report.loading}
        error={lookupError}
        onEmployeeChange={setEmployeeId}
        onDepartmentChange={setDepartmentId}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      <UtilizationReportTable
        rows={report.rows}
        loading={report.loading}
        page={page}
        pageSize={pageSize}
        totalItems={report.totalElements}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
      />
    </PageShell>
  );
}
